import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  IconUsers, IconUser, IconHome, IconCalendar, IconReceipt,
  IconLock, IconDownload, IconFlame, IconX,
} from '../components/Icons';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function daysAgo(d) {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (days === 0) return 'dziś';
  if (days === 1) return 'wczoraj';
  if (days < 7) return `${days} dni temu`;
  if (days < 30) return `${Math.floor(days / 7)} tyg. temu`;
  return `${Math.floor(days / 30)} mies. temu`;
}

function engagementScore(u) {
  // 0–100: im więcej danych, tym wyższy score
  const pts = Math.min(u.liczba_klientow * 10, 40)
    + Math.min(u.liczba_spacerow * 5, 40)
    + (u.last_activity ? 20 : 0);
  return Math.min(pts, 100);
}

// ─── Małe komponenty ──────────────────────────────────────────────────────────

function Chip({ label, color = 'var(--bg4)', textColor = 'var(--ink3)' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
      fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em',
      textTransform: 'uppercase', background: color, color: textColor,
    }}>{label}</span>
  );
}

function PlanChip({ plan, stripeStatus }) {
  if (plan === 'pro') return <Chip label="Pro" color="var(--acc)" textColor="#fff" />;
  return <Chip label="Trial" />;
}

function StatusChip({ user }) {
  if (user.is_blocked) return <Chip label="Zablokowany" color="rgba(239,68,68,0.15)" textColor="var(--err2)" />;
  if (!user.email_verified) return <Chip label="Niezweryfikowany" color="rgba(245,158,11,0.15)" textColor="#f59e0b" />;
  return <Chip label="Aktywny" color="rgba(34,197,94,0.12)" textColor="var(--ok2)" />;
}

function EngagementBar({ score }) {
  const color = score >= 60 ? 'var(--ok2)' : score >= 30 ? '#f59e0b' : 'var(--err2)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ flex: 1, height: '4px', background: 'var(--bg4)', borderRadius: '2px' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: '2px', transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: '10px', color: 'var(--ink3)', minWidth: '24px' }}>{score}</span>
    </div>
  );
}

function StatCard({ label, value, sub, accent, Icon }) {
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</p>
        {Icon && <Icon className="w-4 h-4" style={{ color: 'var(--ink3)', opacity: 0.7 }} />}
      </div>
      <p style={{ fontSize: '26px', fontWeight: 700, color: accent || 'var(--ink)', lineHeight: 1 }}>{value}</p>
      {sub && <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '6px' }}>{sub}</p>}
    </div>
  );
}

// ─── Modal potwierdzenia ──────────────────────────────────────────────────────

function ConfirmModal({ title, message, danger, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px 32px', maxWidth: '400px', width: '90%' }}>
        <p style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink)', marginBottom: '10px' }}>{title}</p>
        <p style={{ fontSize: '12px', color: 'var(--ink2)', marginBottom: '24px', lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
          <button onClick={onConfirm} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: danger ? 'var(--err2)' : 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Potwierdź</button>
        </div>
      </div>
    </div>
  );
}

// ─── Karta użytkownika ────────────────────────────────────────────────────────

function UserCard({ userId, onBack, onUpdated }) {
  const { impersonate } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, label, message }
  const [newPlan, setNewPlan] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.adminGetUser(userId).then(d => { setData(d); setNewPlan(d.user.plan); }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      await api.adminPatchUser(userId, payload);
      await load();
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    const type = confirm.type;
    setConfirm(null);
    if (type === 'block')    await patch({ is_blocked: 1 });
    if (type === 'unblock')  await patch({ is_blocked: 0 });
    if (type === 'delete')   { await api.adminDeleteUser(userId); onBack(); onUpdated?.(); }
    if (type === 'plan')     await patch({ plan: newPlan });
  };

  if (loading) return <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0' }}>Ładowanie…</p>;
  if (!data) return null;

  const { user, engagement } = data;
  const score = engagementScore({ ...engagement });
  const isActive = !user.is_blocked && user.email_verified;

  return (
    <div>
      {confirm && (
        <ConfirmModal
          title={confirm.label}
          message={confirm.message}
          danger={confirm.danger}
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '6px 14px', cursor: 'pointer' }}>
          ← Powrót
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.imie || user.email}
          </h2>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: '2px 0 0' }}>{user.email}{user.firma ? ` · ${user.firma}` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <PlanChip plan={user.plan} stripeStatus={user.stripe_status} />
          <StatusChip user={user} />
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--err2)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#fff', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          {error} <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><IconX className="w-3 h-3" /></button>
        </div>
      )}

      <div className="admin-detail-grid">

        {/* Lewa kolumna */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Profil */}
          <section style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '14px' }}>Profil konta</p>
            {[
              ['ID', `#${user.id}`],
              ['Firma', user.firma],
              ['Telefon', user.telefon],
              ['NIP', user.nip],
              ['Kraj', user.kraj],
              ['Waluta', user.waluta],
              ['Rejestracja', fmtDate(user.created_at)],
              ['Na trialu', `${engagement.days_since_registration} dni`],
            ].map(([label, val]) => val ? (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '9px', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: 'var(--ink3)', flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: '11px', color: 'var(--ink)', fontWeight: 500, textAlign: 'right' }}>{val}</span>
              </div>
            ) : null)}
          </section>

          {/* Zmień plan */}
          <section style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '12px' }}>Plan SaaS</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {['trial', 'pro'].map(p => (
                <button key={p} disabled={saving}
                  onClick={() => { setNewPlan(p); setConfirm({ type: 'plan', label: `Zmień plan na ${p.toUpperCase()}`, message: `Konto ${user.email} zostanie przełączone na plan ${p.toUpperCase()}.`, danger: false }); }}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: `1px solid ${user.plan === p ? 'var(--acc)' : 'var(--border)'}`, background: user.plan === p ? 'var(--acc)' : 'var(--bg4)', color: user.plan === p ? '#fff' : 'var(--ink2)', fontSize: '11px', fontWeight: 700, cursor: user.plan === p ? 'default' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {p}
                </button>
              ))}
            </div>
            {user.stripe_customer_id ? (
              <div style={{ fontSize: '10px', color: 'var(--ink3)', padding: '8px', background: 'var(--bg4)', borderRadius: '6px' }}>
                <div>Stripe ID: <span style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{user.stripe_customer_id}</span></div>
                {user.stripe_status && <div style={{ marginTop: '4px' }}>Status: <span style={{ color: 'var(--ok2)', fontWeight: 600 }}>{user.stripe_status}</span></div>}
              </div>
            ) : (
              <p style={{ fontSize: '10px', color: 'var(--ink3)', fontStyle: 'italic' }}>Brak połączenia ze Stripe</p>
            )}
          </section>

          {/* Akcje */}
          <section style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '12px' }}>Akcje</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button disabled={saving || user.is_blocked} onClick={() => impersonate(userId)} style={{ padding: '9px 14px', borderRadius: '6px', border: '1px solid #6d28d9', background: 'transparent', color: '#a78bfa', fontSize: '12px', fontWeight: 600, cursor: user.is_blocked ? 'not-allowed' : 'pointer', opacity: user.is_blocked ? 0.5 : 1 }}>
                Wejdź jako ten użytkownik
              </button>
              {user.is_blocked ? (
                <button disabled={saving} onClick={() => setConfirm({ type: 'unblock', label: 'Odblokuj konto', message: `Konto ${user.email} zostanie odblokowane. Użytkownik odzyska dostęp.`, danger: false })}>
                  Odblokuj konto
                </button>
              ) : (
                <button disabled={saving} onClick={() => setConfirm({ type: 'block', label: 'Zablokuj konto', message: `Konto ${user.email} zostanie zablokowane. Użytkownik nie będzie mógł się zalogować.`, danger: true })}
                  style={{ padding: '9px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--err2)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  Zablokuj konto
                </button>
              )}
              <button disabled={saving} onClick={() => setConfirm({ type: 'delete', label: 'Usuń konto', message: `Trwałe usunięcie konta ${user.email} wraz ze wszystkimi danymi. Operacji nie można cofnąć.`, danger: true })}
                style={{ padding: '9px 14px', borderRadius: '6px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                Usuń konto
              </button>
            </div>
          </section>
        </div>

        {/* Prawa kolumna */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Engagement */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
            <StatCard label="Klienci" value={engagement.liczba_klientow} Icon={IconUsers} />
            <StatCard label="Spacery" value={engagement.liczba_spacerow} sub={`${engagement.aktywne_spacery} aktywnych`} Icon={IconHome} />
            <StatCard label="Zdarzenia" value={engagement.liczba_zdarzen} Icon={IconCalendar} />
            <StatCard label="Faktury" value={engagement.liczba_platnosci} Icon={IconReceipt} />
          </div>

          {/* Aktywność */}
          <section style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '16px' }}>Aktywność i zaangażowanie</p>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--ink2)' }}>Engagement score</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }}>{score}/100</span>
              </div>
              <EngagementBar score={score} />
            </div>

            <div className="form-row-2">
              {[
                ['Ostatnia aktywność', daysAgo(engagement.last_activity) || 'brak danych'],
                ['Zarejestrowany', fmtDate(user.created_at)],
                ['Dni od rejestracji', `${engagement.days_since_registration} dni`],
                ['Email zweryfikowany', user.email_verified ? 'Tak' : 'Nie'],
              ].map(([label, val]) => (
                <div key={label}>
                  <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '2px' }}>{label}</p>
                  <p style={{ fontSize: '13px', color: 'var(--ink)', fontWeight: 600 }}>{val}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stripe / Billing */}
          <section style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Billing / Stripe</p>
              <Chip label="Wkrótce" color="rgba(99,102,241,0.15)" textColor="#818cf8" />
            </div>
            <div className="form-row-2" style={{ opacity: 0.6 }}>
              {[
                ['Stripe Customer ID', user.stripe_customer_id || '—'],
                ['Subscription ID', user.stripe_subscription_id || '—'],
                ['Status subskrypcji', user.stripe_status || '—'],
                ['Wygaśnięcie planu', fmtDate(user.plan_expires_at)],
              ].map(([label, val]) => (
                <div key={label}>
                  <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '2px' }}>{label}</p>
                  <p style={{ fontSize: '12px', color: 'var(--ink)', fontWeight: 500, fontFamily: val.startsWith('cus_') || val.startsWith('sub_') ? 'monospace' : 'inherit' }}>{val}</p>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '14px', fontStyle: 'italic' }}>
              Integracja Stripe zostanie uruchomiona przy wdrożeniu płatności.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}

// ─── Lista użytkowników ───────────────────────────────────────────────────────

function UsersList({ users, loading, onSelect, onRefresh }) {
  const { impersonate } = useAuth();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [actionId, setActionId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [error, setError] = useState(null);

  const patch = async (id, data) => {
    setActionId(id);
    try { await api.adminPatchUser(id, data); onRefresh(); }
    catch (e) { setError(e.message); }
    finally { setActionId(null); }
  };

  const handleConfirmDelete = async () => {
    const { id } = confirm;
    setConfirm(null);
    setActionId(id);
    try { await api.adminDeleteUser(id); onRefresh(); }
    catch (e) { setError(e.message); }
    finally { setActionId(null); }
  };

  let filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !search || u.email.toLowerCase().includes(q) || (u.imie || '').toLowerCase().includes(q) || (u.firma || '').toLowerCase().includes(q);
    const matchPlan = planFilter === 'all' || u.plan === planFilter || (planFilter === 'blocked' && u.is_blocked) || (planFilter === 'inactive' && !u.last_activity);
    return matchSearch && matchPlan;
  });

  filtered = [...filtered].sort((a, b) => {
    if (sort === 'newest')   return new Date(b.created_at) - new Date(a.created_at);
    if (sort === 'oldest')   return new Date(a.created_at) - new Date(b.created_at);
    if (sort === 'activity') return (b.last_activity || '0') > (a.last_activity || '0') ? 1 : -1;
    if (sort === 'clients')  return b.liczba_klientow - a.liczba_klientow;
    return 0;
  });

  return (
    <div>
      {confirm && (
        <ConfirmModal title="Usuń konto" message={`Trwałe usunięcie konta ${confirm.email}. Operacji nie można cofnąć.`} danger
          onConfirm={handleConfirmDelete} onCancel={() => setConfirm(null)} />
      )}
      {error && (
        <div style={{ background: 'var(--err2)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#fff', marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
          {error} <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><IconX className="w-3 h-3" /></button>
        </div>
      )}

      {/* Filtry */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Szukaj po emailu, imieniu, firmie…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 12px', outline: 'none' }} />
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '8px 12px', cursor: 'pointer', outline: 'none' }}>
          <option value="all">Wszyscy ({users.length})</option>
          <option value="trial">Trial ({users.filter(u => u.plan === 'trial').length})</option>
          <option value="pro">Pro ({users.filter(u => u.plan === 'pro').length})</option>
          <option value="blocked">Zablokowani ({users.filter(u => u.is_blocked).length})</option>
          <option value="inactive">Bez aktywności ({users.filter(u => !u.last_activity).length})</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '8px 12px', cursor: 'pointer', outline: 'none' }}>
          <option value="newest">Najnowsi</option>
          <option value="oldest">Najstarsi</option>
          <option value="activity">Ostatnia aktywność</option>
          <option value="clients">Liczba klientów</option>
        </select>
      </div>

      {loading ? <p style={{ color: 'var(--ink3)', fontSize: '13px' }}>Ładowanie…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Użytkownik', 'Plan', 'Status', 'Klienci', 'Spacery', 'Rejestracja', 'Ost. aktywność', 'Akcje'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', opacity: u.is_blocked ? 0.55 : 1 }}>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => onSelect(u.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, color: 'var(--acc2)', fontSize: '12px', textDecoration: 'underline', textUnderlineOffset: '2px' }}>{u.email}</div>
                      {(u.imie || u.firma) && <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '1px' }}>{[u.imie, u.firma].filter(Boolean).join(' · ')}</div>}
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px' }}><PlanChip plan={u.plan} /></td>
                  <td style={{ padding: '10px 12px' }}><StatusChip user={u} /></td>
                  <td style={{ padding: '10px 12px', color: 'var(--ink2)', textAlign: 'center', fontWeight: u.liczba_klientow > 0 ? 600 : 400 }}>{u.liczba_klientow}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--ink2)', textAlign: 'center' }}>{u.liczba_spacerow}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--ink3)', whiteSpace: 'nowrap', fontSize: '11px' }}>{fmtDate(u.created_at)}</td>
                  <td style={{ padding: '10px 12px', color: u.last_activity ? 'var(--ink2)' : 'var(--err2)', whiteSpace: 'nowrap', fontSize: '11px' }}>
                    {daysAgo(u.last_activity) || 'brak'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {u.is_admin !== 1 && (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={() => onSelect(u.id)} style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Karta</button>
                        <button disabled={actionId === u.id || u.is_blocked} onClick={() => impersonate(u.id)}
                          style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #6d28d9', background: 'transparent', color: '#a78bfa', fontSize: '11px', fontWeight: 600, cursor: u.is_blocked ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: u.is_blocked ? 0.4 : 1 }}>
                          Wejdź
                        </button>
                        <button disabled={actionId === u.id} onClick={() => patch(u.id, { is_blocked: u.is_blocked ? 0 : 1 })}
                          style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg4)', color: u.is_blocked ? 'var(--ok2)' : 'var(--err2)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {u.is_blocked ? 'Odblokuj' : 'Blokuj'}
                        </button>
                        <button disabled={actionId === u.id} onClick={() => setConfirm({ id: u.id, email: u.email })}
                          style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                          Usuń
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: 'var(--ink3)', fontSize: '12px' }}>Brak użytkowników spełniających kryteria</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Przegląd (SaaS dashboard) ────────────────────────────────────────────────

function Overview({ stats, loading }) {
  if (loading) return <p style={{ color: 'var(--ink3)', fontSize: '13px' }}>Ładowanie…</p>;
  if (!stats) return null;

  const conversionRate = stats.total_users > 0 ? Math.round((stats.users_pro / stats.total_users) * 100) : 0;
  const mNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Top metryki */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
        <StatCard label="Użytkownicy łącznie" value={stats.total_users} sub={`+${stats.new_users_30d} ostatnie 30 dni`} Icon={IconUser} />
        <StatCard label="Plan Trial" value={stats.users_trial} sub={`${100 - conversionRate}% bazy`} />
        <StatCard label="Plan Pro" value={stats.users_pro} sub={`${conversionRate}% konwersja`} accent="var(--acc)" />
        <StatCard label="Zablokowani" value={stats.users_blocked} accent={stats.users_blocked > 0 ? 'var(--err2)' : undefined} Icon={IconLock} />
        <StatCard label="Spacery w systemie" value={stats.total_spacery} sub={`${stats.active_spacery} aktywnych`} Icon={IconHome} />
        <StatCard label="Klienci w systemie" value={stats.total_klienci} Icon={IconUsers} />
      </div>

      {/* Konwersja trial → pro */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Konwersja Trial → Pro</p>
          <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--acc)' }}>{conversionRate}%</span>
        </div>
        <div style={{ height: '8px', background: 'var(--bg4)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${conversionRate}%`, height: '100%', background: 'var(--acc)', borderRadius: '4px', transition: 'width 0.5s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{stats.users_trial} na trialu</span>
          <span style={{ fontSize: '11px', color: 'var(--acc)', fontWeight: 600 }}>{stats.users_pro} pro</span>
        </div>
      </div>

      {/* Wykres rejestracji */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
        <p style={{ fontSize: '11px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '20px' }}>
          Nowe rejestracje — ostatnie 6 miesięcy
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '100px' }}>
          {stats.rejestracje.map((r, i) => {
            const max = Math.max(...stats.rejestracje.map(x => x.count), 1);
            const h = Math.max((r.count / max) * 80, r.count > 0 ? 6 : 2);
            const isLast = i === stats.rejestracje.length - 1;
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: isLast ? 700 : 400, color: isLast ? 'var(--ink)' : 'var(--ink3)' }}>{r.count}</span>
                <div title={`${mNames[r.miesiac - 1]} ${r.rok}: ${r.count}`}
                  style={{ width: '100%', height: `${h}px`, background: isLast ? 'var(--acc)' : 'var(--bg4)', borderRadius: '4px 4px 0 0', transition: 'height 0.3s', cursor: 'default' }} />
                <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{mNames[r.miesiac - 1]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Placeholder MRR */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Revenue (MRR / ARR)</p>
          <Chip label="Po integracji Stripe" color="rgba(99,102,241,0.15)" textColor="#818cf8" />
        </div>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', fontStyle: 'italic' }}>
          Metryki przychodu pojawią się automatycznie po podłączeniu Stripe. Dane będą obejmować MRR, ARR, churn, LTV i historię płatności per użytkownik.
        </p>
      </div>

      <BackupCard />

    </div>
  );
}

function BackupCard() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleDownload = async () => {
    setError('');
    setLoading(true);
    try {
      const blob = await api.adminBackupDb();
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scanpilots-backup-${stamp}.db`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Nie udało się pobrać kopii');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Backup bazy danych
          </p>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>
            Pobierz kopię całej bazy SQLite. Auto-backup mailem codziennie o 3:00.
          </p>
        </div>
        <button
          onClick={handleDownload}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'var(--acc)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 18px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
          }}>
          {loading ? 'Pobieranie…' : <><IconDownload className="w-3.5 h-3.5" /> Pobierz backup</>}
        </button>
      </div>
      {error && (
        <p style={{ fontSize: '12px', color: 'var(--err2)', margin: '8px 0 0' }}>{error}</p>
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

// ─── Tab Finanse ──────────────────────────────────────────────────────────────

const CURRENCIES = ['PLN', 'USD', 'EUR'];
const CYCLES = { monthly: 'Miesięczny', yearly: 'Roczny' };
const STATUSES = { paid: 'Opłacona', pending: 'Oczekuje', overdue: 'Zaległa' };
const METHODS = { transfer: 'Przelew', card: 'Karta', stripe: 'Stripe', cash: 'Gotówka' };

const fmtMoney = (n, cur = 'PLN') => `${Number(n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

function SaasFinanse() {
  const toast = useToast();
  const confirm = useConfirm();
  const [report, setReport]     = useState(null);
  const [payments, setPayments] = useState([]);
  const [config, setConfig]     = useState(null);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [subTab, setSubTab]     = useState('report');
  const [showForm, setShowForm] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [localConfig, setLocalConfig]   = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, c, u] = await Promise.all([
        api.adminGetSaasReport(),
        api.adminGetSaasPayments(),
        api.adminGetSaasConfig(),
        api.adminGetUsers(),
      ]);
      setReport(r); setPayments(p); setConfig(c); setLocalConfig(c);
      setUsers(u.filter(u => !u.is_admin));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try { await api.adminSaveSaasConfig(localConfig); await load(); }
    catch (e) { setError(e.message); }
    finally { setSavingConfig(false); }
  };

  const deletePayment = async (id) => {
    const ok = await confirm({
      title: 'Usuń płatność',
      message: 'Czy na pewno chcesz usunąć tę płatność? Tej akcji nie można cofnąć.',
      confirmLabel: 'Usuń',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adminDeleteSaasPayment(id);
      toast.success('Usunięto');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const subTabStyle = (id) => ({
    padding: '5px 14px', border: 'none', borderRadius: '5px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
    background: subTab === id ? 'var(--bg)' : 'transparent',
    color: subTab === id ? 'var(--ink)' : 'var(--ink3)',
    boxShadow: subTab === id ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
  });

  if (loading) return <p style={{ color: 'var(--ink3)', fontSize: '13px' }}>Ładowanie…</p>;

  const mNames = ['Sty','Lut','Mar','Kwi','Maj','Cze','Lip','Sie','Wrz','Paź','Lis','Gru'];

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--err2)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#fff', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          {error} <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}><IconX className="w-3 h-3" /></button>
        </div>
      )}

      {/* Sub-nawigacja */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
        <button style={subTabStyle('report')}   onClick={() => setSubTab('report')}>Raport</button>
        <button style={subTabStyle('payments')} onClick={() => setSubTab('payments')}>Płatności ({payments.length})</button>
        <button style={subTabStyle('trial')}    onClick={() => setSubTab('trial')}>Trial pipeline ({report?.trial_pipeline?.length || 0})</button>
        <button style={subTabStyle('config')}   onClick={() => setSubTab('config')}>Konfiguracja cen</button>
      </div>

      {/* ── RAPORT ── */}
      {subTab === 'report' && report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* MRR cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {Object.entries(report.mrr).map(([cur, val]) => (
              <div key={cur} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' }}>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>MRR ({cur})</p>
                <p style={{ fontSize: '24px', fontWeight: 700, color: 'var(--acc)', lineHeight: 1 }}>{fmtMoney(val, cur)}</p>
                <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '4px' }}>ARR: {fmtMoney(val * 12, cur)}</p>
              </div>
            ))}
            {Object.keys(report.mrr).length === 0 && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' }}>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>MRR</p>
                <p style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ink3)', lineHeight: 1 }}>—</p>
                <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '4px' }}>Brak aktywnych subskrypcji</p>
              </div>
            )}
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' }}>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>Przychód łączny (PLN)</p>
              <p style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>{fmtMoney(report.total_revenue_pln, 'PLN')}</p>
              <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '4px' }}>{report.total_payments} płatności</p>
            </div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' }}>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>Aktywni Pro</p>
              <p style={{ fontSize: '24px', fontWeight: 700, color: 'var(--ok2)', lineHeight: 1 }}>{report.pro_users.length}</p>
              <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '4px' }}>użytkowników</p>
            </div>
          </div>

          {/* Wykres przychodów */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px' }}>
            <p style={{ fontSize: '11px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '20px' }}>Przychód miesięczny — ostatnie 12 miesięcy</p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '110px' }}>
              {report.monthly_revenue.map((r, i) => {
                const max = Math.max(...report.monthly_revenue.map(x => x.revenue), 1);
                const h = Math.max((r.revenue / max) * 90, r.revenue > 0 ? 6 : 2);
                const isLast = i === report.monthly_revenue.length - 1;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                    <span style={{ fontSize: '10px', color: isLast ? 'var(--ink)' : 'var(--ink3)', fontWeight: isLast ? 700 : 400 }}>
                      {r.revenue > 0 ? Math.round(r.revenue) : ''}
                    </span>
                    <div title={`${mNames[r.miesiac - 1]} ${r.rok}: ${r.revenue} (${r.count} płatności)`}
                      style={{ width: '100%', height: `${h}px`, background: isLast ? 'var(--acc)' : 'var(--bg4)', borderRadius: '4px 4px 0 0', cursor: 'default' }} />
                    <span style={{ fontSize: '9px', color: 'var(--ink3)' }}>{mNames[r.miesiac - 1]}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pro users */}
          {report.pro_users.length > 0 && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }}>Aktywne subskrypcje Pro</p>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Użytkownik', 'Aktywny do', 'Łącznie zapłacone', 'Cykl'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.pro_users.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '12px' }}>{u.email}</div>
                        {(u.imie || u.firma) && <div style={{ fontSize: '10px', color: 'var(--ink3)' }}>{[u.imie, u.firma].filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td style={{ padding: '10px 14px', color: u.active_until >= new Date().toISOString().slice(0,10) ? 'var(--ok2)' : 'var(--err2)', fontWeight: 600, fontSize: '12px' }}>
                        {fmtDate(u.active_until)}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--ink)' }}>{fmtMoney(u.total_paid, u.currency || 'PLN')}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--ink2)', fontSize: '11px' }}>{CYCLES[u.billing_cycle] || u.billing_cycle || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PŁATNOŚCI ── */}
      {subTab === 'payments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
            <button onClick={() => { setEditPayment(null); setShowForm(true); }}
              style={{ padding: '8px 18px', borderRadius: '7px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
              + Dodaj płatność
            </button>
          </div>

          {showForm && (
            <PaymentForm
              payment={editPayment}
              users={users}
              config={config}
              onSave={async (data) => {
                if (editPayment) await api.adminUpdateSaasPayment(editPayment.id, data);
                else await api.adminAddSaasPayment(data);
                setShowForm(false); setEditPayment(null); await load();
              }}
              onCancel={() => { setShowForm(false); setEditPayment(null); }}
            />
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Użytkownik', 'Kwota', 'Okres', 'Cykl', 'Metoda', 'Status', 'Akcje'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '12px' }}>{p.email}</div>
                      {(p.imie || p.firma) && <div style={{ fontSize: '10px', color: 'var(--ink3)' }}>{[p.imie, p.firma].filter(Boolean).join(' · ')}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtMoney(p.amount, p.currency)}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink2)', fontSize: '11px', whiteSpace: 'nowrap' }}>{p.period_from} – {p.period_to}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink3)', fontSize: '11px' }}>{CYCLES[p.billing_cycle] || p.billing_cycle}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink3)', fontSize: '11px' }}>{METHODS[p.payment_method] || p.payment_method}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                        background: p.status === 'paid' ? 'rgba(34,197,94,0.12)' : p.status === 'pending' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                        color: p.status === 'paid' ? 'var(--ok2)' : p.status === 'pending' ? '#f59e0b' : 'var(--err2)',
                      }}>{STATUSES[p.status] || p.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => { setEditPayment(p); setShowForm(true); }}
                          style={{ padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '11px', cursor: 'pointer' }}>Edytuj</button>
                        <button onClick={() => deletePayment(p.id)}
                          style={{ padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '11px', cursor: 'pointer' }}>Usuń</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--ink3)', fontSize: '12px' }}>Brak płatności — dodaj pierwszą powyżej</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TRIAL PIPELINE ── */}
      {subTab === 'trial' && report && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            {[
              ['Wszyscy na trialu', report.trial_pipeline.length],
              ['Gorące leady', report.trial_pipeline.filter(u => u.hot).length, 'var(--acc)'],
              ['Wygasłe', report.trial_pipeline.filter(u => u.expired).length, 'var(--err2)'],
              ['Aktywne', report.trial_pipeline.filter(u => !u.expired).length, 'var(--ok2)'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px' }}>
                <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</p>
                <p style={{ fontSize: '22px', fontWeight: 700, color: color || 'var(--ink)', lineHeight: 1 }}>{val}</p>
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Użytkownik', 'Dni trialu', 'Pozostało dni', 'Spacery', 'Pozostało spacerów', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.trial_pipeline.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', background: u.expired ? 'rgba(239,68,68,0.04)' : u.hot ? 'rgba(204,104,64,0.06)' : 'transparent' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '12px' }}>{u.email}</div>
                      {(u.imie || u.firma) && <div style={{ fontSize: '10px', color: 'var(--ink3)' }}>{[u.imie, u.firma].filter(Boolean).join(' · ')}</div>}
                      {u.hot && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', color: 'var(--acc)', fontWeight: 700 }}><IconFlame className="w-3 h-3" /> GORĄCY LEAD</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink2)' }}>{u.daysUsed}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: u.daysLeft <= 3 ? 'var(--err2)' : u.daysLeft <= 7 ? '#f59e0b' : 'var(--ok2)' }}>
                      {u.expired && u.daysLeft === 0 ? <span style={{ color: 'var(--err2)' }}>Wygasł</span> : u.daysLeft}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink2)' }}>{u.liczba_spacerow}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: u.spaceLeft <= 1 ? 'var(--err2)' : u.spaceLeft <= 3 ? '#f59e0b' : 'var(--ok2)' }}>
                      {u.spaceLeft === 0 ? <span style={{ color: 'var(--err2)' }}>Limit</span> : u.spaceLeft}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {u.expired
                        ? <Chip label="Wygasły" color="rgba(239,68,68,0.12)" textColor="var(--err2)" />
                        : u.hot
                          ? <Chip label="Gorący" color="rgba(204,104,64,0.15)" textColor="var(--acc)" />
                          : <Chip label="Aktywny" color="rgba(34,197,94,0.12)" textColor="var(--ok2)" />
                      }
                    </td>
                  </tr>
                ))}
                {report.trial_pipeline.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: 'var(--ink3)', fontSize: '12px' }}>Brak użytkowników na trialu</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── KONFIGURACJA ── */}
      {subTab === 'config' && localConfig && (
        <div style={{ maxWidth: '560px' }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '24px', marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '20px' }}>Ceny planu Pro</p>
            <div className="form-row-3" style={{ marginBottom: '20px' }}>
              {CURRENCIES.map(cur => (
                <div key={cur}>
                  <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '8px', fontWeight: 600 }}>Miesięczny ({cur})</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="number" min="0" step="0.01"
                      value={localConfig[`price_monthly_${cur.toLowerCase()}`] || ''}
                      onChange={e => setLocalConfig(p => ({ ...p, [`price_monthly_${cur.toLowerCase()}`]: e.target.value }))}
                      style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '8px 10px', outline: 'none' }} />
                    <span style={{ fontSize: '11px', color: 'var(--ink3)', flexShrink: 0 }}>{cur}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="form-row-3">
              {CURRENCIES.map(cur => (
                <div key={cur}>
                  <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '8px', fontWeight: 600 }}>Roczny /mies ({cur})</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="number" min="0" step="0.01"
                      value={localConfig[`price_yearly_${cur.toLowerCase()}`] || ''}
                      onChange={e => setLocalConfig(p => ({ ...p, [`price_yearly_${cur.toLowerCase()}`]: e.target.value }))}
                      style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '8px 10px', outline: 'none' }} />
                    <span style={{ fontSize: '11px', color: 'var(--ink3)', flexShrink: 0 }}>{cur}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '24px', marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '20px' }}>Limity trialu</p>
            <div className="form-row-2" style={{ gap: '16px' }}>
              <div>
                <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '8px', fontWeight: 600 }}>Czas trwania (dni)</p>
                <input type="number" min="1" max="365"
                  value={localConfig.trial_days || ''}
                  onChange={e => setLocalConfig(p => ({ ...p, trial_days: e.target.value }))}
                  style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '8px 10px', outline: 'none' }} />
              </div>
              <div>
                <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '8px', fontWeight: 600 }}>Maks. spacerów</p>
                <input type="number" min="1" max="1000"
                  value={localConfig.trial_max_spacery || ''}
                  onChange={e => setLocalConfig(p => ({ ...p, trial_max_spacery: e.target.value }))}
                  style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '8px 10px', outline: 'none' }} />
              </div>
            </div>
          </div>

          <button disabled={savingConfig} onClick={saveConfig}
            style={{ padding: '10px 24px', borderRadius: '7px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: savingConfig ? 0.6 : 1 }}>
            {savingConfig ? 'Zapisywanie…' : 'Zapisz konfigurację'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Formularz płatności ──────────────────────────────────────────────────────

function PaymentForm({ payment, users, config, onSave, onCancel }) {
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    user_id:        payment?.user_id    || '',
    amount:         payment?.amount     || '',
    currency:       payment?.currency   || 'PLN',
    billing_cycle:  payment?.billing_cycle  || 'monthly',
    period_from:    payment?.period_from    || today,
    period_to:      payment?.period_to     || '',
    status:         payment?.status         || 'paid',
    payment_method: payment?.payment_method || 'transfer',
    notes:          payment?.notes          || '',
  });
  const [saving, setSaving] = useState(false);

  const autoFillAmount = () => {
    if (!form.currency || !form.billing_cycle) return;
    const key = `price_${form.billing_cycle}_${form.currency.toLowerCase()}`;
    const price = config?.[key];
    if (price) setForm(p => ({ ...p, amount: price }));
  };

  const autoFillPeriodTo = () => {
    if (!form.period_from || !form.billing_cycle) return;
    const d = new Date(form.period_from);
    if (form.billing_cycle === 'monthly') d.setMonth(d.getMonth() + 1);
    else d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    setForm(p => ({ ...p, period_to: d.toISOString().slice(0, 10) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); }
    catch (err) { toast.error(err.message); setSaving(false); }
  };

  const inp = (label, field, type = 'text', extra = {}) => (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>{label}</p>
      <input type={type} value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} {...extra}
        style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );

  const sel = (label, field, options) => (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>{label}</p>
      <select value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '8px 10px', outline: 'none', cursor: 'pointer' }}>
        {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
      </select>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
      <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '16px' }}>{payment ? 'Edytuj płatność' : 'Nowa płatność SaaS'}</p>
      <div className="form-row-2" style={{ marginBottom: '12px' }}>
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Użytkownik</p>
          <select required value={form.user_id} onChange={e => setForm(p => ({ ...p, user_id: e.target.value }))}
            style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '8px 10px', outline: 'none', cursor: 'pointer' }}>
            <option value="">Wybierz użytkownika…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.email}{u.firma ? ` (${u.firma})` : ''}</option>)}
          </select>
        </div>
        {sel('Waluta', 'currency', CURRENCIES.map(c => [c, c]))}
        {sel('Cykl', 'billing_cycle', Object.entries(CYCLES))}
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Kwota</p>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input required type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              style={{ flex: 1, background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none' }} />
            <button type="button" onClick={autoFillAmount} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink3)', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Auto</button>
          </div>
        </div>
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Okres od</p>
          <input required type="date" value={form.period_from} onChange={e => setForm(p => ({ ...p, period_from: e.target.value }))}
            style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Okres do</p>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input required type="date" value={form.period_to} onChange={e => setForm(p => ({ ...p, period_to: e.target.value }))}
              style={{ flex: 1, background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none' }} />
            <button type="button" onClick={autoFillPeriodTo} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink3)', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Auto</button>
          </div>
        </div>
        {sel('Status', 'status', Object.entries(STATUSES))}
        {sel('Metoda', 'payment_method', Object.entries(METHODS))}
      </div>
      {inp('Notatki', 'notes')}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button type="button" onClick={onCancel} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
        <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Zapisywanie…' : payment ? 'Zapisz zmiany' : 'Dodaj płatność'}
        </button>
      </div>
    </form>
  );
}


// ─── Beta invites — outreach do fotografów ───────────────────────────────────

// Solidne kolory chipów — czytelne na light i dark theme. Używamy istniejących
// w projekcie var(--ok2) / var(--err2) (zdefiniowane dla obu motywów w index.css).
const INVITE_STATUS = {
  nowy:           { label: 'Nowy',           bg: 'var(--bg4)', fg: 'var(--ink)' },
  odpowiedzial:   { label: 'Odpowiedział',   bg: '#f59e0b',    fg: '#fff' },
  wyslany_dostep: { label: 'Wysłany dostęp', bg: 'var(--ok2)', fg: '#fff' },
  odrzucony:      { label: 'Odrzucony',      bg: 'var(--err2)', fg: '#fff' },
};

const SOURCE_LABEL = { landing: 'Landing form', manual: 'Ręcznie' };

// Pill pokazujący status wysyłki danego maila (E1/E2/E3). Hover → tooltip z datą.
// Wysłane: solidne zielone tło + biały tekst (czytelne na light/dark theme).
// Niewysłane: bg4 + ink2 (też czytelne, wyraźnie odróżnia się od "wysłane").
function EmailBadge({ label, sentAt, auto, sublabel }) {
  const sent = !!sentAt;
  const title = sent
    ? `Wysłane ${new Date(sentAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}${auto ? ' (auto)' : ''}${sublabel ? ' · ' + sublabel : ''}`
    : 'Niewysłane';
  return (
    <span title={title}
      style={{
        display: 'inline-block', minWidth: '28px', textAlign: 'center',
        padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700,
        letterSpacing: '0.04em',
        background: sent ? 'var(--ok2)' : 'var(--bg4)',
        color: sent ? '#fff' : 'var(--ink2)',
        border: '1px solid ' + (sent ? 'var(--ok2)' : 'var(--border)'),
      }}>
      {label}{auto ? '*' : ''}
    </span>
  );
}

function InviteForm({ invite, onSave, onCancel }) {
  const toast = useToast();
  const [form, setForm] = useState({
    email:   invite?.email   || '',
    imie:    invite?.imie    || '',
    firma:   invite?.firma   || '',
    strona:  invite?.strona  || '',
    notatki: invite?.notatki || '',
    status:  invite?.status  || 'nowy',
    send_intro: !invite, // domyślnie ON dla nowych, OFF przy edycji
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); }
    catch (err) { toast.error(err.message); setSaving(false); }
  };

  const inp = (label, field, type = 'text', required = false) => (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>{label}</p>
      <input type={type} required={required} value={form[field]}
        onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <form onSubmit={submit} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
      <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '16px' }}>{invite ? 'Edytuj zaproszenie' : 'Nowe zaproszenie'}</p>
      <div className="form-row-2" style={{ marginBottom: '12px' }}>
        {inp('Email', 'email', 'email', true)}
        {inp('Imię', 'imie')}
        {inp('Firma', 'firma')}
        {inp('Strona', 'strona')}
        <div>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Status</p>
          <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
            style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink2)', fontSize: '12px', padding: '8px 10px', outline: 'none', cursor: 'pointer' }}>
            {Object.entries(INVITE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Notatki (prywatne)</p>
        <textarea value={form.notatki} onChange={e => setForm(p => ({ ...p, notatki: e.target.value }))} rows={2}
          style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      {!invite && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', fontSize: '12px', color: 'var(--ink2)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={form.send_intro}
            onChange={e => setForm(p => ({ ...p, send_intro: e.target.checked }))}
            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--acc)' }} />
          Wyślij od razu Email 1 (zaproszenie do testowania)
        </label>
      )}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <button type="button" onClick={onCancel}
          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
        <button type="submit" disabled={saving}
          style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Zapisywanie…' : invite ? 'Zapisz' : 'Dodaj'}
        </button>
      </div>
    </form>
  );
}

// Modal "Wyślij dostęp" z opcjonalnym custom message — np. odpowiedź na pytania
// fotografa zadane w odpowiedzi na E1.
function GrantAccessModal({ invite, onCancel, onConfirm }) {
  const [message, setMessage] = useState('');
  const [busy, setBusy]       = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await onConfirm(message.trim()); }
    catch { setBusy(false); }
  };

  return (
    <div onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '520px', width: '100%', padding: '24px 26px', boxShadow: 'var(--shadow-modal)' }}>
        <p style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>Wyślij dostęp do bety</p>
        <p style={{ fontSize: '12px', color: 'var(--ink2)', marginBottom: '18px', lineHeight: 1.5 }}>
          Konto dla <strong style={{ color: 'var(--ink)' }}>{invite.email}</strong> zostanie utworzone, wygenerujemy hasło tymczasowe i wyślemy maila z dostępem.
        </p>

        <div style={{ marginBottom: '6px' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink2)', marginBottom: '4px', fontWeight: 600 }}>Wiadomość dodatkowa (opcjonalnie)</p>
          <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '6px', lineHeight: 1.5 }}>
            Jeśli fotograf pytał o coś w odpowiedzi na zaproszenie — odpowiedz tutaj. Tekst zostanie wstawiony w środku maila przed sekcją "po 2-3 dniach odpisz".
          </p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={5}
            placeholder="Dzięki za pytanie o X — chciałem się upewnić że..."
            style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '12px', padding: '10px 12px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
          <button onClick={onCancel} disabled={busy}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>
            Anuluj
          </button>
          <button onClick={submit} disabled={busy}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Wysyłam…' : 'Wyślij dostęp'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BetaInvites() {
  const toast = useToast();
  const confirm = useConfirm();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [filter, setFilter]     = useState('all');
  const [busyId, setBusyId]     = useState(null);
  const [grantTarget, setGrantTarget] = useState(null); // invite któremu wysyłamy dostęp

  const load = useCallback(() => {
    setLoading(true);
    api.adminGetInvites().then(setInvites).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    if (editing) await api.adminUpdateInvite(editing.id, data);
    else         await api.adminAddInvite(data);
    setShowForm(false);
    setEditing(null);
    load();
    toast.success(editing ? 'Zapisano zmiany' : (data.send_intro ? 'Dodano i wysłano Email 1' : 'Dodano zaproszenie'));
  };

  // Otwiera modal z textarea zamiast prostego confirm — admin może dodać wiadomość
  // do maila E2 (np. odpowiedź na pytania fotografa zadane przy okazji E1).
  const grant = (inv) => setGrantTarget(inv);

  const confirmGrant = async (customMessage) => {
    const inv = grantTarget;
    setGrantTarget(null);
    setBusyId(inv.id);
    try {
      await api.adminGrantAccess(inv.id, customMessage);
      toast.success('Dostęp wysłany na ' + inv.email);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const sendIntro = async (inv) => {
    const ok = await confirm({
      title: 'Wysłać Email 1 (zaproszenie)?',
      message: `Email z zaproszeniem do testowania zostanie wysłany na ${inv.email}.`,
      confirmLabel: 'Wyślij',
    });
    if (!ok) return;
    setBusyId(inv.id);
    try {
      await api.adminSendIntro(inv.id);
      toast.success('Email 1 wysłany');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const sendFeedback = async (inv) => {
    const ok = await confirm({
      title: 'Wysłać Email 3 (pytania feedback)?',
      message: `Email z pytaniami feedback zostanie wysłany na ${inv.email}.`,
      confirmLabel: 'Wyślij',
    });
    if (!ok) return;
    setBusyId(inv.id);
    try {
      await api.adminSendFeedback(inv.id);
      toast.success('Email 3 wysłany');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const removeInvite = async (inv) => {
    const ok = await confirm({
      title: 'Usunąć zaproszenie?',
      message: `Usunięcie wpisu dla ${inv.email}. ${inv.user_id ? 'Konto użytkownika NIE zostanie usunięte.' : ''}`,
      danger: true,
    });
    if (!ok) return;
    try { await api.adminDeleteInvite(inv.id); load(); }
    catch (e) { toast.error(e.message); }
  };

  const filtered = filter === 'all' ? invites : invites.filter(i => i.status === filter);
  const counts = {
    all:            invites.length,
    odpowiedzial:   invites.filter(i => i.status === 'odpowiedzial').length,
    nowy:           invites.filter(i => i.status === 'nowy').length,
    wyslany_dostep: invites.filter(i => i.status === 'wyslany_dostep').length,
    odrzucony:      invites.filter(i => i.status === 'odrzucony').length,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>Beta zaproszenia</p>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>Outreach do fotografów Matterport. <strong style={{ color: 'var(--ink2)' }}>E1</strong> (zaproszenie) wysyłane przy dodaniu lub ręcznie · <strong style={{ color: 'var(--ink2)' }}>E2</strong> (dostęp) ręcznie po odpowiedzi · <strong style={{ color: 'var(--ink2)' }}>E3</strong> (feedback) auto po 7 dniach od pierwszego logowania (lub ręcznie).</p>
        </div>
        {!showForm && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Dodaj zaproszenie
          </button>
        )}
      </div>

      {showForm && (
        <InviteForm
          invite={editing}
          onSave={save}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {grantTarget && (
        <GrantAccessModal
          invite={grantTarget}
          onCancel={() => setGrantTarget(null)}
          onConfirm={confirmGrant}
        />
      )}

      {/* Filtry-kafelki */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          ['all',            'Wszystkie',     null],
          ['odpowiedzial',   'Odpowiedzieli', '#f59e0b'],
          ['nowy',           'Nowi',          'var(--ink2)'],
          ['wyslany_dostep', 'Mają dostęp',   'var(--ok2)'],
          ['odrzucony',      'Odrzuceni',     'var(--err2)'],
        ].map(([key, label, color]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{
              background: filter === key ? 'var(--bg3)' : 'var(--bg2)',
              border: '1px solid ' + (filter === key ? 'var(--acc)' : 'var(--border)'),
              borderRadius: '10px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s',
            }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</p>
            <p style={{ fontSize: '20px', fontWeight: 700, color: color || 'var(--ink)', lineHeight: 1, margin: 0 }}>{counts[key]}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0' }}>Ładowanie…</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0', textAlign: 'center' }}>
          {filter === 'all' ? 'Brak zaproszeń. Dodaj pierwsze powyżej lub czekaj na zgłoszenia z landingu.' : 'Brak wpisów w tym statusie.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Kontakt', 'Status', 'Wysyłki', 'Daty', 'Aktywność konta', 'Notatki', 'Akcje'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const st = INVITE_STATUS[inv.status] || INVITE_STATUS.nowy;
                const hasAccount = !!inv.user_id;
                const busy = busyId === inv.id;
                return (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '12px' }}>{inv.email}</div>
                      {(inv.imie || inv.firma) && (
                        <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>
                          {[inv.imie, inv.firma].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {inv.strona && (
                        <a href={inv.strona.startsWith('http') ? inv.strona : `https://${inv.strona}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: '10px', color: 'var(--acc)', textDecoration: 'none' }}>
                          {inv.strona}
                        </a>
                      )}
                      <div style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '2px' }}>
                        {SOURCE_LABEL[inv.source] || inv.source}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <Chip label={st.label} color={st.bg} textColor={st.fg} />
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <EmailBadge label="E1" sentAt={inv.email1_sent_at} />
                        <EmailBadge label="E2" sentAt={inv.access_sent_at} />
                        <EmailBadge label="E3" sentAt={inv.email3_sent_at} auto={!!inv.email3_auto} />
                      </div>
                      {inv.email3_auto === 1 && <div style={{ fontSize: '9px', color: 'var(--ink3)', marginTop: '4px' }}>* auto-wysyłka</div>}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'var(--ink2)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                      <div>Dodano: {fmtDate(inv.created_at)}</div>
                      {inv.responded_at && <div style={{ color: '#f59e0b' }}>Odpowiedź: {fmtDate(inv.responded_at)}</div>}
                      {inv.first_login_at && <div style={{ color: 'var(--ok2)' }}>1. login: {fmtDate(inv.first_login_at)}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '11px' }}>
                      {hasAccount ? (
                        <div>
                          <div style={{ color: 'var(--ink2)' }}>{inv.liczba_klientow || 0} klientów · {inv.liczba_spacerow || 0} spacerów</div>
                          <div style={{ color: 'var(--ink3)', fontSize: '10px' }}>
                            {inv.last_login_at ? `Login: ${daysAgo(inv.last_login_at)}` : 'Nigdy nie logował'}
                          </div>
                          {inv.last_activity && (
                            <div style={{ color: 'var(--ink3)', fontSize: '10px' }}>
                              Akt: {daysAgo(inv.last_activity)}
                            </div>
                          )}
                          {inv.is_blocked === 1 && <div style={{ color: 'var(--err2)', fontSize: '10px', fontWeight: 700 }}>Zablokowane</div>}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--ink3)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '11px', color: 'var(--ink2)', maxWidth: '180px' }}>
                      {inv.notatki ? <span title={inv.notatki}>{inv.notatki.length > 60 ? inv.notatki.slice(0, 60) + '…' : inv.notatki}</span> : <span style={{ color: 'var(--ink3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
                        {!inv.email1_sent_at && inv.status !== 'odrzucony' && (
                          <button onClick={() => sendIntro(inv)} disabled={busy}
                            style={{ padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1 }}>
                            Wyślij E1
                          </button>
                        )}
                        {inv.status !== 'odrzucony' && (
                          <button onClick={() => grant(inv)} disabled={busy}
                            style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1 }}>
                            {busy ? 'Wysyłam…' : inv.status === 'wyslany_dostep' ? 'Wyślij E2 ponownie' : 'Wyślij E2 (dostęp)'}
                          </button>
                        )}
                        {inv.status === 'wyslany_dostep' && !inv.email3_sent_at && (
                          <button onClick={() => sendFeedback(inv)} disabled={busy}
                            style={{ padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: busy ? 0.6 : 1 }}>
                            Wyślij E3 (feedback)
                          </button>
                        )}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => { setEditing(inv); setShowForm(true); }}
                            style={{ flex: 1, padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', cursor: 'pointer' }}>Edytuj</button>
                          <button onClick={() => removeInvite(inv)}
                            style={{ flex: 1, padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '10px', cursor: 'pointer' }}>Usuń</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Leady (cold outreach) ────────────────────────────────────────────────────
// Pool leadów ze scraperów Matterport (UK/IE/...) + opt-inów z landingu.
// Wysyłka cold_invite_en przez hello@scanpilots.com; po wysyłce status='outreach_sent'.

const LEAD_STATUS_DISPLAY = {
  imported:      { label: 'Imported',      bg: 'var(--bg4)',   fg: 'var(--ink2)' },
  outreach_sent: { label: 'Sent',          bg: '#1e3a5f',      fg: '#93c5fd' },
  opened:        { label: 'Opened',        bg: '#155e75',      fg: '#67e8f9' },
  clicked:       { label: 'Clicked',       bg: '#581c87',      fg: '#c4b5fd' },
  replied:       { label: 'Replied',       bg: '#78350f',      fg: '#fcd34d' },
  converted:     { label: 'Converted',     bg: 'var(--ok2)',   fg: '#ffffff' },
  blacklisted:   { label: 'Blacklisted',   bg: 'var(--err2)',  fg: '#ffffff' },
  bounced:       { label: 'Bounced',       bg: 'var(--err2)',  fg: '#ffffff' },
};

const LEAD_COUNTRIES = ['UK','IE','US','AU','NZ','CA','DE','NL','FR','ES','IT','PL'];

function LeadsImportModal({ onClose, onImported }) {
  const toast = useToast();
  const [csv, setCsv]         = useState('');
  const [country, setCountry] = useState('UK');
  const [source, setSource]   = useState('csv_import');
  const [busy, setBusy]       = useState(false);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.readAsText(file);
  };

  const submit = async () => {
    if (!csv.trim()) { toast.error('Wklej lub załaduj CSV'); return; }
    setBusy(true);
    try {
      const r = await api.adminImportLeads({ csv, country, source });
      toast.success(`Import OK: ${r.imported} new, ${r.updated} updated, ${r.skipped_existing + r.skipped_invalid + r.skipped_domain} skipped`);
      onImported();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '720px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--ink)' }}>Import CSV leadów</h3>
          <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--ink3)' }}>
            Format ze scrapera: <code style={{ fontSize: '10px' }}>name, city, address, phone, email, email_source, website, google_maps_url, rating, reviews_count, place_id</code>
          </p>
        </div>
        <div style={{ padding: '20px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Country</span>
              <select value={country} onChange={e => setCountry(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px' }}>
                {LEAD_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Source (tag)</span>
              <input value={source} onChange={e => setSource(e.target.value)} placeholder="np. google_maps_uk_2026_06"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
            </label>
          </div>
          <label>
            <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Plik CSV (lub wklej w polu poniżej)</span>
            <input type="file" accept=".csv,text/csv" onChange={e => onFile(e.target.files?.[0])}
              style={{ fontSize: '12px', color: 'var(--ink2)' }} />
          </label>
          <textarea value={csv} onChange={e => setCsv(e.target.value)} rows={12}
            placeholder="name,city,address,phone,email,email_source,website,google_maps_url,rating,reviews_count,place_id"
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '11px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
          <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0 }}>
            UPSERT po email — duplikat ze statusem <code>imported</code> zostanie zaktualizowany, inne statusy pominięte. Maile z <code>matterport.com</code> i <code>scanpilots.com</code> automatycznie skipowane.
          </p>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
          <button onClick={submit} disabled={busy || !csv.trim()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: busy || !csv.trim() ? 0.6 : 1 }}>
            {busy ? 'Importuję…' : 'Importuj'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadFormModal({ lead, onCancel, onSave }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email:   lead?.email   || '',
    imie:    lead?.imie    || '',
    firma:   lead?.firma   || '',
    website: lead?.website || '',
    miasto:  lead?.miasto  || '',
    country: lead?.country || 'UK',
    phone:   lead?.phone   || '',
    notatki: lead?.notatki || '',
  });
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.email.trim()) { toast.error('Email jest wymagany'); return; }
    setBusy(true);
    try {
      // Backend nie pozwala zmienić emaila w PATCH (jest po unique), więc przy edit
      // wysyłamy bez emaila. Przy add — pełny payload.
      if (lead) {
        const { email, ...patch } = form;
        await api.adminUpdateLead(lead.id, patch);
      } else {
        await api.adminAddLead(form);
      }
      toast.success(lead ? 'Zapisano' : 'Dodano leada');
      onSave();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const fields = [
    { key: 'email',   label: 'Email *',  full: false, disabled: !!lead },
    { key: 'imie',    label: 'Imię' },
    { key: 'firma',   label: 'Firma' },
    { key: 'miasto',  label: 'Miasto' },
    { key: 'country', label: 'Country', kind: 'select' },
    { key: 'website', label: 'Website', full: true },
    { key: 'phone',   label: 'Telefon' },
    { key: 'notatki', label: 'Notatki', full: true, kind: 'textarea' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '520px', width: '100%' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--ink)' }}>{lead ? 'Edytuj leada' : 'Dodaj leada'}</h3>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {fields.map(f => (
            <label key={f.key} style={{ gridColumn: f.full ? '1 / span 2' : 'auto' }}>
              <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>{f.label}</span>
              {f.kind === 'textarea' ? (
                <textarea value={form[f.key]} onChange={e => change(f.key, e.target.value)} rows={3}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box', resize: 'vertical' }} />
              ) : f.kind === 'select' ? (
                <select value={form[f.key]} onChange={e => change(f.key, e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px' }}>
                  {LEAD_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input value={form[f.key]} disabled={f.disabled} onChange={e => change(f.key, e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: f.disabled ? 'var(--bg2)' : 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box', opacity: f.disabled ? 0.6 : 1 }} />
              )}
            </label>
          ))}
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
          <button onClick={submit} disabled={busy || !form.email.trim()}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: busy || !form.email.trim() ? 0.6 : 1 }}>
            {busy ? 'Zapisuję…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleBatchModal({ count, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    interval_min: 8,
    jitter_min:   2,
    hours_start:  9,
    hours_end:    17,
    skip_weekend: true,
  });
  const [busy, setBusy] = useState(false);
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try { await onSubmit(form); }
    finally { setBusy(false); }
  };

  // Szacowany czas trwania: count × interval (bez jitter, bez przesunięć poza window)
  const minutesTotal = (count - 1) * form.interval_min;
  const hoursTotal   = Math.floor(minutesTotal / 60);
  const minsRest     = minutesTotal % 60;
  const durationStr  = hoursTotal > 0 ? `${hoursTotal}h ${minsRest}min` : `${minsRest}min`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '480px', width: '100%' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--ink)' }}>Zakolejkuj {count} {count === 1 ? 'leada' : 'leadów'}</h3>
          <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--ink3)' }}>
            Cron worker w backendzie wyśle 1 mail co interval ± jitter, tylko w godzinach biznesowych UTC. Pierwszy mail pójdzie do ~1 minuty od kliknięcia "Zakolejkuj", kolejne wg slotów.
          </p>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <label>
            <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Interval (min)</span>
            <input type="number" min="1" max="1440" value={form.interval_min} onChange={e => change('interval_min', parseInt(e.target.value, 10) || 1)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
          </label>
          <label>
            <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Jitter (±min)</span>
            <input type="number" min="0" max="60" value={form.jitter_min} onChange={e => change('jitter_min', parseInt(e.target.value, 10) || 0)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
          </label>
          <label>
            <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Hours start (UTC)</span>
            <input type="number" min="0" max="23" value={form.hours_start} onChange={e => change('hours_start', parseInt(e.target.value, 10) || 0)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
          </label>
          <label>
            <span style={{ fontSize: '11px', color: 'var(--ink3)', display: 'block', marginBottom: '4px' }}>Hours end (UTC)</span>
            <input type="number" min="1" max="24" value={form.hours_end} onChange={e => change('hours_end', parseInt(e.target.value, 10) || 1)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
          </label>
          <label style={{ gridColumn: '1 / span 2', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <input type="checkbox" checked={form.skip_weekend} onChange={e => change('skip_weekend', e.target.checked)} />
            <span style={{ fontSize: '12px', color: 'var(--ink2)' }}>Pomiń weekend (sob/niedz)</span>
          </label>
          <p style={{ gridColumn: '1 / span 2', fontSize: '11px', color: 'var(--ink3)', margin: '4px 0 0' }}>
            Szacowany czas trwania: <strong style={{ color: 'var(--ink2)' }}>{durationStr}</strong> (bez przerw weekend / poza window).
            Cron sprawdza co minutę — pierwszy mail wyjdzie w ciągu ~60s.
          </p>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>Anuluj</button>
          <button onClick={submit} disabled={busy}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Kolejkuję…' : 'Zakolejkuj'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Leads() {
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData]               = useState({ rows: [], total: 0, summary: {} });
  const [loading, setLoading]         = useState(true);
  const [busyId, setBusyId]           = useState(null);
  const [filter, setFilter]           = useState('all');
  const [countryFilter, setCountryFilter] = useState('UK');
  const [search, setSearch]           = useState('');
  const [showImport, setShowImport]   = useState(false);
  const [showAdd, setShowAdd]         = useState(false);
  const [editing, setEditing]         = useState(null);
  const [selected, setSelected]       = useState(() => new Set());
  const [queueCount, setQueueCount]   = useState(0);
  const [showSchedule, setShowSchedule] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = { limit: 500 };
    if (filter !== 'all')           q.status  = filter;
    if (countryFilter !== 'all')    q.country = countryFilter;
    if (search.trim())              q.search  = search.trim();
    api.adminGetLeads(q)
      .then(setData)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
    api.adminGetLeadsQueue()
      .then(r => setQueueCount(r.count))
      .catch(() => {});
  }, [toast, filter, countryFilter, search]);

  const toggleSelect = (id) => setSelected(s => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });
  const toggleSelectAll = () => setSelected(s => {
    const visible = data.rows.filter(r => r.status === 'imported' && !r.scheduled_send_at);
    const allSelected = visible.length > 0 && visible.every(r => s.has(r.id));
    if (allSelected) return new Set();
    return new Set(visible.map(r => r.id));
  });
  const clearSelection = () => setSelected(new Set());

  const scheduleBatch = async (cfg) => {
    const ids = Array.from(selected);
    try {
      const r = await api.adminScheduleBatchLeads({ lead_ids: ids, ...cfg });
      const skipped = r.skipped_already_sent + r.skipped_blacklisted + r.skipped_already_queued + r.skipped_not_found;
      const firstStr = r.first_at ? new Date(r.first_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
      const lastStr  = r.last_at  ? new Date(r.last_at).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
      toast.success(`Zakolejkowano ${r.scheduled}${skipped ? `, pominięto ${skipped}` : ''}. Start: ${firstStr}, ostatni: ${lastStr}`);
      setShowSchedule(false);
      clearSelection();
      load();
    } catch (e) { toast.error(e.message); }
  };

  const unschedule = async (lead) => {
    setBusyId(lead.id);
    try { await api.adminUnscheduleLead(lead.id); load(); toast.success('Usunięto z kolejki'); }
    catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const clearQueue = async () => {
    const ok = await confirm({
      title: 'Wyczyścić całą kolejkę?',
      message: `${queueCount} pending wysyłek zostanie anulowanych. Status leadów wraca na 'imported'.`,
      danger: true,
    });
    if (!ok) return;
    try { await api.adminUnscheduleBatchLeads({ all: true }); load(); toast.success('Kolejka wyczyszczona'); }
    catch (e) { toast.error(e.message); }
  };

  // Debounce search — input zmienia state każdym znakiem, ale fetch odpalamy
  // dopiero po 300ms ciszy. Filtr/country aktywują natychmiast (debounce daje 0ms).
  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const sendInvite = async (lead) => {
    const ok = await confirm({
      title: 'Wysłać cold invite?',
      message: `Email z template "cold_invite_en" pójdzie na ${lead.email} przez hello@scanpilots.com.`,
      confirmLabel: 'Wyślij',
    });
    if (!ok) return;
    setBusyId(lead.id);
    try {
      await api.adminSendLeadInvite(lead.id);
      toast.success('Invite wysłany');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  const blacklist = async (lead) => {
    const ok = await confirm({
      title: 'Dodać do czarnej listy?',
      message: `${lead.email} — przyszłe akcje send-invite będą blokowane.`,
      danger: true,
    });
    if (!ok) return;
    try { await api.adminUpdateLead(lead.id, { status: 'blacklisted' }); load(); toast.success('Blacklisted'); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async (lead) => {
    const ok = await confirm({
      title: 'Usunąć leada?',
      message: `Hard delete ${lead.email} + cascade całej historii outreach.`,
      danger: true,
    });
    if (!ok) return;
    try { await api.adminDeleteLead(lead.id); load(); }
    catch (e) { toast.error(e.message); }
  };

  const sumKeys = ['imported','outreach_sent','opened','clicked','replied','converted','blacklisted','bounced'];
  const summaryTotal = sumKeys.reduce((a, k) => a + (data.summary?.[k] || 0), 0);

  // Scheduled to nie status, tylko liczba leadów z scheduled_send_at != null.
  // Kafelek nie zmienia filtra (filter dotyczy status), to wskaźnik dla admina.
  const tiles = [
    ['all',           'Wszystkie',     'var(--ink2)', summaryTotal,                false],
    ['imported',      'Imported',      'var(--ink2)', data.summary?.imported      || 0, false],
    ['__scheduled',   'Scheduled',     '#f97316',     queueCount,                  true],
    ['outreach_sent', 'Outreach sent', '#93c5fd',     data.summary?.outreach_sent || 0, false],
    ['opened',        'Opened',        '#67e8f9',     data.summary?.opened        || 0, false],
    ['clicked',       'Clicked',       '#c4b5fd',     data.summary?.clicked       || 0, false],
    ['replied',       'Replied',       '#fcd34d',     data.summary?.replied       || 0, false],
    ['converted',     'Converted',     'var(--ok2)',  data.summary?.converted     || 0, false],
    ['blacklisted',   'Blacklisted',   'var(--err2)', data.summary?.blacklisted   || 0, false],
  ];

  // Liczy ile widocznych leadów można zaznaczyć (imported, niezaplanowanych)
  const eligibleVisible = data.rows.filter(r => r.status === 'imported' && !r.scheduled_send_at);
  const allSelectedVisible = eligibleVisible.length > 0 && eligibleVisible.every(r => selected.has(r.id));
  const formatSchedule = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>Leady (cold outreach)</p>
          <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0, maxWidth: '780px' }}>
            Pool leadów ze scraperów Matterport (UK / IE / …) + opt-inów z landingu.
            Wysyłka cold invite (template <code style={{ fontSize: '11px', background: 'var(--bg4)', padding: '1px 5px', borderRadius: '3px' }}>cold_invite_en</code>) przez <code style={{ fontSize: '11px', background: 'var(--bg4)', padding: '1px 5px', borderRadius: '3px' }}>hello@scanpilots.com</code>.
            Po wysyłce status leada przechodzi na <strong>outreach_sent</strong>. Gdy lead klika CTA na landingu i wypełnia formularz, auto-onboarding tworzy konto i wysyła dostęp od razu.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowImport(true)}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Import CSV
          </button>
          <button onClick={() => { setEditing(null); setShowAdd(true); }}
            style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Dodaj ręcznie
          </button>
        </div>
      </div>

      {showImport && <LeadsImportModal onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); load(); }} />}
      {showAdd && <LeadFormModal lead={editing} onCancel={() => { setShowAdd(false); setEditing(null); }} onSave={() => { setShowAdd(false); setEditing(null); load(); }} />}
      {showSchedule && <ScheduleBatchModal count={selected.size} onCancel={() => setShowSchedule(false)} onSubmit={scheduleBatch} />}

      {/* Bulk-action bar / queue summary */}
      {(selected.size > 0 || queueCount > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--acc)', borderRadius: '8px', flexWrap: 'wrap' }}>
          {selected.size > 0 && (
            <>
              <span style={{ fontSize: '12px', color: 'var(--ink)', fontWeight: 600 }}>
                Zaznaczonych: {selected.size}
              </span>
              <button onClick={() => setShowSchedule(true)}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                Zakolejkuj {selected.size}
              </button>
              <button onClick={clearSelection}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink2)', fontSize: '11px', cursor: 'pointer' }}>
                Wyczyść selekcję
              </button>
              <span style={{ flex: 1 }} />
            </>
          )}
          {queueCount > 0 && (
            <>
              {selected.size === 0 && <span style={{ flex: 1 }} />}
              <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>
                W kolejce: <strong style={{ color: '#f97316' }}>{queueCount}</strong>
              </span>
              <button onClick={clearQueue}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '11px', cursor: 'pointer' }}>
                Wyczyść kolejkę
              </button>
            </>
          )}
        </div>
      )}

      {/* Filtry-kafelki per status. Scheduled (isInfo) nie zmienia filtra. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px', marginBottom: '16px' }}>
        {tiles.map(([key, label, color, count, isInfo]) => (
          <button key={key} onClick={isInfo ? undefined : () => setFilter(key)} disabled={isInfo}
            style={{
              background: filter === key ? 'var(--bg3)' : 'var(--bg2)',
              border: '1px solid ' + (filter === key ? 'var(--acc)' : 'var(--border)'),
              borderRadius: '8px', padding: '10px 12px', textAlign: 'left', cursor: isInfo ? 'default' : 'pointer', transition: 'all 0.15s',
              opacity: isInfo ? 0.92 : 1,
            }}>
            <p style={{ fontSize: '9px', color: 'var(--ink3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</p>
            <p style={{ fontSize: '18px', fontWeight: 700, color, lineHeight: 1, margin: 0 }}>{count}</p>
          </button>
        ))}
      </div>

      {/* Country + search */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px' }}>
          <option value="all">Wszystkie kraje</option>
          {LEAD_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="search" placeholder="Szukaj email / firma / miasto…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--ink)', fontSize: '12px', boxSizing: 'border-box' }} />
      </div>

      {/* Tabela */}
      {loading ? (
        <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0' }}>Ładowanie…</p>
      ) : data.rows.length === 0 ? (
        <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0', textAlign: 'center' }}>
          Brak leadów. Zaimportuj CSV ze scrapera (np. matterport_uk_final.csv) lub dodaj ręcznie.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'center', padding: '8px 6px', width: '32px' }}>
                  <input type="checkbox" checked={allSelectedVisible} onChange={toggleSelectAll}
                    disabled={eligibleVisible.length === 0}
                    title={eligibleVisible.length === 0 ? 'Brak imported leadów do zaznaczenia' : 'Zaznacz/odznacz wszystkie widoczne imported'} />
                </th>
                {['Firma / Miasto', 'Email', 'Country', 'Status', 'Outreach / Scheduled', 'Source', 'Akcje'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map(lead => {
                const busy = busyId === lead.id;
                const status = LEAD_STATUS_DISPLAY[lead.status] || { label: lead.status, bg: 'var(--bg4)', fg: 'var(--ink2)' };
                const canSend = lead.status === 'imported' && !lead.scheduled_send_at;
                const isScheduled = !!lead.scheduled_send_at;
                const eligibleToSelect = lead.status === 'imported' && !lead.scheduled_send_at;
                return (
                  <tr key={lead.id} style={{ borderBottom: '1px solid var(--border)', background: selected.has(lead.id) ? 'rgba(204,104,64,0.08)' : 'transparent' }}>
                    <td style={{ padding: '10px 6px', verticalAlign: 'top', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleSelect(lead.id)}
                        disabled={!eligibleToSelect}
                        title={!eligibleToSelect ? 'Tylko leady ze statusem Imported i bez zaplanowanej wysyłki' : ''} />
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{lead.firma || <span style={{ color: 'var(--ink3)' }}>—</span>}</div>
                      <div style={{ fontSize: '11px', color: 'var(--ink2)' }}>{lead.miasto || ''}</div>
                      {lead.website && (
                        <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: '10px', color: 'var(--acc)', textDecoration: 'none' }}>
                          {lead.website.replace(/^https?:\/\//, '').slice(0, 32)}
                        </a>
                      )}
                      {lead.rating != null && <div style={{ fontSize: '10px', color: 'var(--ink3)' }}>{lead.rating} ({lead.reviews_count || 0})</div>}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '11px', color: 'var(--ink)' }}>{lead.email}</td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '11px', color: 'var(--ink2)' }}>{lead.country}</td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <Chip label={status.label} color={status.bg} textColor={status.fg} />
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '11px', color: 'var(--ink2)' }}>
                      {isScheduled ? (
                        <div style={{ color: '#f97316' }}>
                          <div style={{ fontWeight: 600 }}>Scheduled</div>
                          <div style={{ fontSize: '10px' }}>{formatSchedule(lead.scheduled_send_at)}</div>
                        </div>
                      ) : lead.last_sent_at ? (
                        <div>
                          <div>Sent: {fmtDate(lead.last_sent_at)}</div>
                          {(lead.last_opens > 0 || lead.last_clicks > 0) && (
                            <div style={{ color: 'var(--ink3)', fontSize: '10px' }}>
                              {lead.last_opens || 0} opens · {lead.last_clicks || 0} clicks
                            </div>
                          )}
                          {lead.last_replied_at && <div style={{ color: '#fcd34d', fontSize: '10px' }}>Reply: {fmtDate(lead.last_replied_at)}</div>}
                        </div>
                      ) : <span style={{ color: 'var(--ink3)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top', fontSize: '10px', color: 'var(--ink3)' }}>{lead.source}</td>
                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '130px' }}>
                        {isScheduled ? (
                          <button onClick={() => unschedule(lead)} disabled={busy}
                            style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #f97316', background: 'transparent', color: '#f97316', fontSize: '11px', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                            {busy ? '…' : 'Cofnij'}
                          </button>
                        ) : canSend && (
                          <button onClick={() => sendInvite(lead)} disabled={busy}
                            style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', background: 'var(--acc)', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                            {busy ? 'Wysyłam…' : 'Wyślij teraz'}
                          </button>
                        )}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => { setEditing(lead); setShowAdd(true); }}
                            style={{ flex: 1, padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '10px', cursor: 'pointer' }}>Edytuj</button>
                          {lead.status !== 'blacklisted' && (
                            <button onClick={() => blacklist(lead)} title="Dodaj do czarnej listy"
                              style={{ flex: 1, padding: '3px 8px', borderRadius: '4px', border: '1px solid #f59e0b', background: 'transparent', color: '#f59e0b', fontSize: '10px', cursor: 'pointer' }}>BL</button>
                          )}
                          <button onClick={() => remove(lead)}
                            style={{ flex: 1, padding: '3px 8px', borderRadius: '4px', border: '1px solid var(--err2)', background: 'transparent', color: 'var(--err2)', fontSize: '10px', cursor: 'pointer' }}>Usuń</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.total > data.rows.length && (
            <p style={{ fontSize: '11px', color: 'var(--ink3)', textAlign: 'center', padding: '12px' }}>
              Pokazano {data.rows.length} z {data.total}. Zawęź filtry żeby zobaczyć więcej.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Szablony email ───────────────────────────────────────────────────────────

const TEMPLATE_INFO = {
  intro:          { label: 'Email 1 — Zaproszenie (PL)',     desc: 'Wysyłany przy dodaniu nowego zaproszenia (jeśli zaznaczony checkbox) lub przyciskiem "Wyślij E1".' },
  access:         { label: 'Email 2 — Dostęp do bety (PL)',  desc: 'Wysyłany przyciskiem "Wyślij E2 (dostęp)" po pozytywnej odpowiedzi. Zawiera login i hasło tymczasowe.' },
  feedback:       { label: 'Email 3 — Pytania feedback (PL)', desc: 'Auto-wysyłany 7 dni po pierwszym logowaniu fotografa, lub ręcznie przyciskiem "Wyślij E3".' },
  cold_invite_en: { label: 'Cold invite — anglojęzyczny',    desc: 'Wysyłany do leadów (zakładka "Leady") przez przycisk "Wyślij invite". Soft pitch B2B z linkiem do landingu i opt-out przez reply.' },
  access_en:      { label: 'Access — anglojęzyczny',         desc: 'Auto-wysyłany do warm leadów którzy klikają CTA na landingu po cold invite. Lustro Email 2 (PL) — login + hasło tymczasowe + quick first steps po angielsku.' },
};

function PreviewModal({ html, subject, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', maxWidth: '720px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '4px' }}>Podgląd · Temat</p>
            <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subject}</p>
          </div>
          <button onClick={onClose}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', cursor: 'pointer' }}>
            Zamknij
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', background: '#0a0a0c', padding: '20px' }}>
          <iframe title="Podgląd email" srcDoc={html}
            style={{ width: '100%', minHeight: '600px', border: 'none', background: 'transparent', display: 'block' }} />
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ template, onSaved }) {
  const toast = useToast();
  const [subject, setSubject] = useState(template.subject);
  const [body,    setBody]    = useState(template.body);
  const [saving,  setSaving]  = useState(false);
  const [resetting, setResetting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const dirty = subject !== template.subject || body !== template.body;
  const info = TEMPLATE_INFO[template.key];

  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpdateEmailTemplate(template.key, { subject, body });
      toast.success('Szablon zapisany');
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      const data = await api.adminPreviewEmailTemplate(template.key, { subject, body });
      setPreview(data);
    } catch (e) { toast.error(e.message); }
    finally { setPreviewing(false); }
  };

  const resetDefault = async () => {
    setResetting(true);
    try {
      await api.adminResetEmailTemplate(template.key);
      toast.success('Przywrócono domyślny szablon');
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setResetting(false); }
  };

  const insertVar = (v) => {
    setBody(prev => prev + `{${v}}`);
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
      {preview && <PreviewModal html={preview.html} subject={preview.subject} onClose={() => setPreview(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>{info?.label || template.key}</p>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: 0, lineHeight: 1.5 }}>{info?.desc}</p>
        </div>
        <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0, whiteSpace: 'nowrap' }}>
          {template.updated_at ? `Zaktualizowano ${new Date(template.updated_at).toLocaleDateString('pl-PL')}` : ''}
        </p>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <p style={{ fontSize: '11px', color: 'var(--ink3)', marginBottom: '4px', fontWeight: 600 }}>Temat</p>
        <input value={subject} onChange={e => setSubject(e.target.value)}
          style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }} />
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '6px' }}>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: 0, fontWeight: 600 }}>Treść</p>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {template.variables.map(v => (
              <button key={v} type="button" onClick={() => insertVar(v)}
                title={`Wstaw {${v}} do treści`}
                style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--acc)', fontSize: '10px', cursor: 'pointer', fontFamily: 'monospace' }}>
                {`{${v}}`}
              </button>
            ))}
          </div>
        </div>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={14}
          style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--ink)', fontSize: '13px', padding: '10px 12px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', lineHeight: 1.6 }} />
        <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '6px 0 0', lineHeight: 1.5 }}>
          Plain text — formatowanie HTML (logo, kolory, ramka) dodawane automatycznie. Newline = nowa linia w mailu. Klikaj zmienne wyżej żeby je wstawić.<br />
          Tekst w <code style={{ background: 'var(--bg4)', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace', color: 'var(--acc)' }}>`backtickach`</code> renderuje się jako monospace code z one-click select (do kopiowania loginu/hasła bez prefixu "mailto:").
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button onClick={resetDefault} disabled={resetting}
          style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink3)', fontSize: '11px', cursor: 'pointer', opacity: resetting ? 0.6 : 1 }}>
          {resetting ? 'Resetuję…' : 'Przywróć domyślny'}
        </button>
        <button onClick={showPreview} disabled={previewing}
          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: previewing ? 0.6 : 1 }}>
          {previewing ? 'Renderuję…' : 'Podgląd'}
        </button>
        <button onClick={save} disabled={!dirty || saving}
          style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: dirty ? 'var(--acc)' : 'var(--bg4)', color: dirty ? '#fff' : 'var(--ink3)', fontSize: '12px', fontWeight: 700, cursor: dirty ? 'pointer' : 'default', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Zapisuję…' : dirty ? 'Zapisz' : 'Bez zmian'}
        </button>
      </div>
    </div>
  );
}

function EmailTemplates() {
  const toast = useToast();
  const [templates, setTemplates] = useState(null);

  const load = useCallback(() => {
    api.adminGetEmailTemplates().then(setTemplates).catch(e => toast.error(e.message));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (!templates) return <p style={{ color: 'var(--ink3)', fontSize: '13px', padding: '32px 0' }}>Ładowanie…</p>;

  // Sortowanie: PL flow (intro, access, feedback) → EN flow (cold_invite_en, access_en)
  const order = ['intro', 'access', 'feedback', 'cold_invite_en', 'access_en'];
  const sorted = [...templates].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>Szablony email</p>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>
          Cztery szablony: trzy dla flow beta-outreach (PL) i cold invite dla leadów (EN). Edytuj treść, zobacz podgląd, zapisz. Zmienne <code style={{ fontSize: '11px', background: 'var(--bg4)', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace', color: 'var(--acc)' }}>{'{imie}'}</code>, <code style={{ fontSize: '11px', background: 'var(--bg4)', padding: '1px 5px', borderRadius: '3px', fontFamily: 'monospace', color: 'var(--acc)' }}>{'{email}'}</code> itp. są podstawiane automatycznie.
        </p>
      </div>
      {sorted.map(tpl => (
        <TemplateEditor key={tpl.key} template={tpl} onSaved={load} />
      ))}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function AdminPanel({ initialTab = 'overview' }) {
  const [tab, setTab] = useState(initialTab);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const loadStats = useCallback(() => {
    setLoadingStats(true);
    api.adminGetStats().then(setStats).catch(console.error).finally(() => setLoadingStats(false));
  }, []);

  const loadUsers = useCallback(() => {
    setLoadingUsers(true);
    api.adminGetUsers().then(setUsers).catch(console.error).finally(() => setLoadingUsers(false));
  }, []);

  useEffect(() => { loadStats(); loadUsers(); }, [loadStats, loadUsers]);

  const refresh = () => { loadStats(); loadUsers(); };

  const tabStyle = (id) => ({
    padding: '7px 20px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    background: tab === id ? 'var(--acc)' : 'var(--bg4)', color: tab === id ? '#fff' : 'var(--ink2)', transition: 'all 0.15s',
  });

  // Widok karty użytkownika
  if (selectedUserId) {
    return (
      <UserCard
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
        onUpdated={refresh}
      />
    );
  }

  return (
    <div>
      {/* Nagłówek */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>ScanPilots Admin</h1>
          <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: '2px 0 0' }}>Zarządzanie SaaS · {users.length} użytkowników</p>
        </div>
      </div>

      {/* Zakładki */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button style={tabStyle('overview')} onClick={() => setTab('overview')}>Przegląd</button>
        <button style={tabStyle('users')} onClick={() => setTab('users')}>
          Użytkownicy {users.length > 0 && `(${users.length})`}
        </button>
        <button style={tabStyle('invites')} onClick={() => setTab('invites')}>Beta zaproszenia</button>
        <button style={tabStyle('leads')} onClick={() => setTab('leads')}>Leady</button>
        <button style={tabStyle('templates')} onClick={() => setTab('templates')}>Szablony email</button>
        <button style={tabStyle('finanse')} onClick={() => setTab('finanse')}>Finanse</button>
      </div>

      {tab === 'overview' && <Overview stats={stats} loading={loadingStats} />}
      {tab === 'users' && (
        <UsersList
          users={users}
          loading={loadingUsers}
          onSelect={setSelectedUserId}
          onRefresh={refresh}
        />
      )}
      {tab === 'invites' && <BetaInvites />}
      {tab === 'leads' && <Leads />}
      {tab === 'templates' && <EmailTemplates />}
      {tab === 'finanse' && <SaasFinanse />}
    </div>
  );
}
